import { useEffect, useMemo, useState } from 'react';
import { CreditManagerCreditPerformanceRow, formatMoney } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getCreditReviewerPerformance } from '../../../../redux/services/reportsService';
import { DillTable, DillColumn, DillSelect } from '../../../general/Dill';

const PERIOD_OPTIONS = [
  { label: 'Decided in last 30 days', value: '30' },
  { label: 'Decided in last 90 days', value: '90' },
  { label: 'Decided in last 180 days', value: '180' },
];

const CreditReviewerPerformanceTable = () => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditReviewerPerformanceReport, loadingList } = useAppSelector((state) => state.reports);
  const [days, setDays] = useState('90');

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(
        getCreditReviewerPerformance({ supplierId: currentSupplier.id, days: Number(days) })
      );
    }
  }, [currentSupplier?.id, days]);

  const isLoading = loadingList.includes('getCreditReviewerPerformance');
  const rows = creditReviewerPerformanceReport?.rows ?? [];
  const periodLabel = PERIOD_OPTIONS.find((option) => option.value === days)?.label ?? '';

  const columns: DillColumn<CreditManagerCreditPerformanceRow>[] = useMemo(
    () => [
      {
        key: 'reviewerName',
        label: 'Credit Manager',
        width: 220,
        sortable: true,
        getSortValue: (row) => row.reviewerName,
        render: (_, row) => <p className="text-xs font-medium text-gray-700">{row.reviewerName}</p>,
      },
      {
        key: 'applicationsDecidedCount',
        label: 'Applications Decided',
        width: 150,
        numeric: true,
        sortable: true,
        defaultSortDir: 'desc',
        render: (v: number) => <p className="text-xs w-full text-right">{v}</p>,
      },
      {
        key: 'applicationsApprovedCount',
        label: 'Approved',
        width: 100,
        numeric: true,
        sortable: true,
        render: (v: number) => <p className="text-xs w-full text-right text-green-600">{v}</p>,
      },
      {
        key: 'applicationsRejectedCount',
        label: 'Rejected',
        width: 100,
        numeric: true,
        sortable: true,
        render: (v: number) => <p className="text-xs w-full text-right text-red-500">{v}</p>,
      },
      {
        key: 'approvalRate',
        label: 'Approval Rate',
        width: 120,
        numeric: true,
        sortable: true,
        getSortValue: (row) => row.approvalRate ?? -1,
        render: (v: number | null) => (
          <p className="text-xs w-full text-right text-gray-700">
            {v != null ? `${Math.round(v * 100)}%` : '—'}
          </p>
        ),
      },
      {
        key: 'averageApprovedAmount',
        label: 'Avg Approved Amount',
        width: 160,
        numeric: true,
        sortable: true,
        getSortValue: (row) => row.averageApprovedAmount ?? -1,
        render: (v: number | null) => (
          <p className="text-xs w-full text-right text-gray-700">
            {v != null ? formatMoney(v, 0) : '—'}
          </p>
        ),
      },
      {
        key: 'averageTimeToDecisionDays',
        label: 'Avg Time to Decision',
        width: 160,
        numeric: true,
        sortable: true,
        getSortValue: (row) => row.averageTimeToDecisionDays ?? -1,
        render: (v: number | null) => (
          <p className="text-xs w-full text-right text-gray-700">{v != null ? `${v} days` : '—'}</p>
        ),
      },
    ],
    []
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-4">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Performance by Credit Manager
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">{periodLabel} · click headers to sort</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="min-w-[170px]">
            <DillSelect value={days} options={PERIOD_OPTIONS} onChange={setDays} />
          </div>
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
            {rows.length} {rows.length === 1 ? 'reviewer' : 'reviewers'}
          </span>
        </div>
      </div>
      <div>
        <DillTable<CreditManagerCreditPerformanceRow>
          columns={columns}
          data={rows}
          rowKey={(row) => row.reviewerId}
          sortable
          search={false}
          loading={isLoading}
          striped={false}
          showCount={false}
          emptyMessage="No credit applications have been decided in this period."
          resizable
        />
      </div>
    </div>
  );
};

export default CreditReviewerPerformanceTable;
